import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import chunk from 'lodash/chunk';
import get from 'lodash/get';
import { FormattedMessage } from 'react-intl';
import { Progress, DropdownItem, Button, Modal, ModalBody } from 'reactstrap';
import { useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';

import ButtonDropdown from 'Components/Utility/ButtonDropdown';
import DropdownToggle from 'Components/Utility/DropdownToggle';
import DropdownMenu from 'Components/Utility/DropdownMenu';
import { getDownloadUrls } from 'Redux/ducks/orders';
import formatFileSize from 'Helpers/Format/FormatFileSize';


const DownloadAllBtn = ({ order }) => {
  const dispatch = useDispatch();
  const [modal, setModal] = useState(false);
  const [selection, setSelection] = useState(false);
  const [status, setStatus] = useState('none');
  const [progress, setProgress] = useState(0);
  const [totalSize, setTotalSize] = useState(0);
  const [totalFiles, setTotalFiles] = useState(0);
  const [fileIncrement, setFileIncrement] = useState(0);
  const [chunkedDownloads, setChunkedDownloads] = useState([]);
  const [buttondownOpen, setButtondownOpen] = useState(false);
  const [availableFormats, setAvailableFormats] = useState([]);


  useEffect(() => {
    const getAvailableFormats = () => {
      const formats = [];
      const orderItems = order?.items || [];

      const extractFormats = (downloads) => {
        downloads.forEach((download) => {
          get(download, 'files', []).forEach((file) => {
            if (!formats.includes(file.format)) {
              formats.push(file.format);
            }
          });
        });
      };

      orderItems.forEach((item) => {
        if (item.type === 'bundle') {
          get(item, 'bundles', []).forEach((tier) => {
            get(tier, 'games', []).forEach((game) => {
              extractFormats(get(game, 'downloads', []));
            });
          });
        } else {
          extractFormats(get(item, 'downloads', []));
        }
      });

      setAvailableFormats(formats);
    };

    if (modal) getAvailableFormats();
  }, [modal, order]);

  useEffect(() => {
    if (status === 'downloaded') {
      setTimeout(() => {
        setStatus('completed');
      }, 2000);
    }
  }, [status]);

  if (!order) return false;

  const toggleModal = () => {
    const newModalState = !modal;

    setModal(newModalState);
    setSelection(newModalState ? false : selection);
    setStatus(newModalState ? 'none' : status);
    setTotalSize(newModalState ? 0 : totalSize);
    setTotalFiles(newModalState ? 0 : totalFiles);
    setChunkedDownloads(newModalState ? 0 : chunkedDownloads);
  };

  const clickDownloadLink = (link) => {
    const element = document.createElement('a');
    document.body.appendChild(element);
    element.setAttribute('href', link.signedGetUrl);
    element.setAttribute('class', 'customDownload');
    element.click();
    element.remove();
  };

  const startDownloads = (downloads) => {
    if (!Array.isArray(downloads) || !downloads.length) {
      return false;
    }

    let totalProgress = 0;
    const chunkedDownloadsArr = chunk(downloads, 5);

    chunkedDownloadsArr.forEach((block, t) => {
      const blockTime = t * 8500; // time before next chunk of downloads 8.5 seconds between
      window.setTimeout(() => {
        block.forEach((link, i) => {
          const time = i * 1500; // time before next downloads starts 1.5 seconds between
          window.setTimeout(() => {
            totalProgress += 1;
            clickDownloadLink(link);
            setProgress((100 / totalFiles) * totalProgress);
            setFileIncrement(totalProgress);
            if (totalFiles === totalProgress) {
              setStatus('downloaded');
              setProgress(100);
            }
          }, time);
        });
      }, blockTime);
    });

    return true;
  };

  const downloadChunk = (e, chunktoDownload) => {
    e.preventDefault();
    startDownloads(chunktoDownload);
  };

  const buttonToggle = () => {
    setButtondownOpen(prevState => !prevState);
  };

  const prepareDownloads = async (e, orderNumber, formatSelection) => {
    e.preventDefault();
    setStatus('preparing');
    const downloadURLs = await dispatch(getDownloadUrls(orderNumber, formatSelection));
    startDownloads(downloadURLs);
  };

  const sizeSelection = (selectedFormat) => {
    const sizeArray = [];
    const orderItems = order?.items || [];

    orderItems.forEach((item) => {
      const isBundle = item.type === 'bundle';

      if (!isEmpty(item.downloads) && !isBundle) {
        get(item, 'downloads', []).forEach((download) => {
          get(download, 'files', []).forEach((file) => {
            if (file.format === selectedFormat) {
              sizeArray.push(file.size);
            }
          });
        });
      }

      if (isBundle) {
        get(item, 'bundles', []).forEach((tier) => {
          get(tier, 'games', []).forEach((game) => {
            get(game, 'downloads', []).forEach((download) => {
              get(download, 'files', []).forEach((file) => {
                if (file.format === selectedFormat) {
                  sizeArray.push(file.size);
                }
              });
            });
          });
        });
      }
    });

    const reducedSizeArray = sizeArray.reduce((a, b) => a + b, 0);

    return { totalSize: formatFileSize(reducedSizeArray), totalFiles: sizeArray.length };
  };

  const selectionFunc = async (e, format) => {
    e.preventDefault();
    const results = sizeSelection(format);
    const chunkCheck = await dispatch(getDownloadUrls(order?._id, selection));
    const chunkCalc = chunk(chunkCheck, 5);

    setSelection(format);
    setTotalSize(results.totalSize);
    setTotalFiles(results.totalFiles);
    setChunkedDownloads(chunkCalc);
  };

  return (
    <React.Fragment>
      <Modal isOpen={modal} toggle={toggleModal} className="account-modal">
        <ModalBody>
          <div className="account-modal-title">
            <FormattedMessage
              id="order.book-download-all-link.header"
              defaultMessage="Download All Files"
            />
          </div>

          <div className="account-modal-form">
            <p className="account-modal-paragraph mb-0">
              <FormattedMessage id="order.book-download-all-link.select-format" defaultMessage="Please select a format below" />
            </p>

            <div className="account-modal-form-buttons pb-2">
              {availableFormats.map(format =>
                <div className="form-button-container small-buttons round-buttons" key={format}>
                  <button className={`btn ${format === selection ? 'btn-success' : 'btn-grey'}`} onClick={e => selectionFunc(e, format)}>
                    <FormattedMessage
                      id="order.book-download-all-link.format-details"
                      defaultMessage="{format}"
                      values={{
                        format,
                      }}
                    />
                  </button>
                </div>,
              )}
            </div>

            {selection !== false &&
              <React.Fragment>
                <p className="account-modal-paragraph mb-0">
                  <strong>
                    <FormattedMessage
                      id="order.book-download-all-link.format-details.total-size"
                      defaultMessage="Total download size:"
                    />&nbsp;
                  </strong>
                  {totalSize === 0 ? 'Select a format' : totalSize}
                </p>
                <p className="account-modal-paragraph mb-0">
                  <strong>
                    <FormattedMessage
                      id="order.book-download-all-link.format-details.total-files"
                      defaultMessage="Total files to download:"
                    />&nbsp;
                  </strong>
                  {totalFiles}
                </p>
              </React.Fragment>
            }

            {(status === 'preparing' || status === 'downloaded') &&
              <div className="my-3">
                <p className="text-center mb-2">
                  <strong>
                    <FormattedMessage
                      id="order.book-download-all-link.format-details.progress-info"
                      defaultMessage="Download Progress - {increment} of {total}"
                      values={{
                        increment: fileIncrement,
                        total: totalFiles,
                      }}
                    />
                  </strong>
                </p>
                <Progress
                  color={progress === 100 ? 'success' : 'primary'}
                  value={progress}
                />

                <p className="text-center mt-2 mb-0 paragraph-small">
                  <FormattedMessage
                    id="order.book-download-all-link.interrupt"
                    defaultMessage="Closing this modal will not interrupt downloads"
                  />
                </p>
              </div>
            }

            <div className="account-modal-form-buttons">
              <div className="form-button-container">
                <button
                  className="btn btn-primary"
                  disabled={!selection || status === 'preparing'}
                  onClick={e => prepareDownloads(e, order?._id, selection)}
                >
                  {((status === 'none' && selection === false) || (status === 'completed' && selection)) &&
                    <FormattedMessage id="order.book-download-all-link.download-now-" defaultMessage="Download All" />
                  }
                  {status === 'none' && selection !== false &&
                    <span>
                      <FormattedMessage
                        id="order.book-download-all-link.download-selection"
                        defaultMessage="Download all {selection} files"
                        values={{
                          selection,
                        }}
                      />
                    </span>
                  }
                  {status === 'preparing' &&
                    <FormattedMessage id="order.book-download-all-link.preparing" defaultMessage="Preparing downloads..." />
                  }
                  {status === 'downloaded' &&
                    <FormattedMessage id="order.book-download-all-link.completed" defaultMessage="Completed" />
                  }
                </button>
              </div>

              {chunkedDownloads.length > 1 &&
                <div className="form-button-container">
                  <ButtonDropdown isOpen={buttondownOpen} toggle={buttonToggle} className="w-100">
                    <DropdownToggle caret color="primary">
                      <FormattedMessage id="order.book-download-all-link.download-chunk" defaultMessage="Download Part" />
                    </DropdownToggle>
                    <DropdownMenu>
                      {chunkedDownloads && chunkedDownloads.map((chunkedData, i) =>
                        <React.Fragment key={Math.random(chunkedData.length)}>
                          <DropdownItem
                            onClick={e => downloadChunk(e, chunkedData)}
                          >
                            <FormattedMessage
                              id="order.book-download-all-link-part.download-selection"
                              defaultMessage="Download {selection}'s Part {part}"
                              values={{
                                selection,
                                part: i + 1,
                              }}
                            />
                          </DropdownItem>
                          {i > 0 &&
                            <DropdownItem divider />
                          }
                        </React.Fragment>,
                      )}
                    </DropdownMenu>
                  </ButtonDropdown>
                </div>
              }

              <div className="form-button-container">
                <button className="btn btn-secondary" onClick={toggleModal}>
                  <FormattedMessage id="order.book-download-all-link.cancel" defaultMessage="Cancel" />
                </button>
              </div>
            </div>

            {window.navigator.userAgent.includes('Firefox') &&
              <p className="account-modal-paragraph paragraph-small text-danger mt-3 mb-0">
                <FormattedMessage
                  id="order.book-download-all-link.firefox-warning"
                  defaultMessage="If you are using Firefox, please note that you will be asked to save every file manually. We recommend using Chrome when downloading all files."
                />
              </p>
            }
            {window.navigator.userAgent.includes('OPR') &&
              <p className="account-modal-paragraph paragraph-small mt-3 mb-0">
                <FormattedMessage
                  id="order.book-download-all-link.opera-warning"
                  defaultMessage="If you are using Opera, please note that you will be asked to save every file manually. We recomment using Chrome when downloading all files."
                />
              </p>
            }
          </div>
        </ModalBody>

        <div className="close-button-container">
          <button aria-label="close" onClick={toggleModal}>
            <FontAwesomeIcon icon={faXmark} title="Close modal" />
          </button>
        </div>
      </Modal>

      <Button color="secondary" className="btn-line-account" onClick={toggleModal}>
        <FormattedMessage id="order.book-download-all-link" defaultMessage="Download All" />
      </Button>
    </React.Fragment>
  );
};

DownloadAllBtn.propTypes = {
  order: PropTypes.shape({}),
};

DownloadAllBtn.defaultProps = {
  order: null,
};


export default DownloadAllBtn;
